import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import equipImg from "@/assets/equip.webp";
import boardGenieImg from "@/assets/board-genie.png";
import dubcircusImg from "@/assets/dubcircus.webp";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";

type ProjectId = "equip" | "boardgenie" | "dubcircus";

interface Project {
  id: ProjectId;
  /** Brand/product name — never translated. */
  name: string;
  url: string;
  stack: string[];
  gradientFrom: string;
  gradientTo: string;
  /** Decorative SVG shown only when there is no image. */
  visual?: React.ReactNode;
  image?: string;
}

interface ProjectData {
  type: string;
  imageAlt: string;
  taglineLead: string;
  taglineEm: string;
  desc: string;
  meta: string;
}

const EquipVisual = () => (
  <svg
    viewBox="0 0 300 200"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 h-full w-full"
  >
    <g transform="translate(245, 36)" stroke="white" strokeWidth="1.2" fill="none" opacity="0.42">
      <circle cx="0" cy="0" r="32" />
      <path d="M 0 -19 L 18 -6 L 11 16 L -11 16 L -18 -6 Z" />
      <path d="M 0 -19 L 0 -32 M 18 -6 L 30 -10 M 11 16 L 18 28 M -11 16 L -18 28 M -18 -6 L -30 -10" />
    </g>
    <g stroke="white" strokeWidth="1.6" fill="none" opacity="0.28" strokeLinecap="round">
      <path d="M -20 178 Q 150 198 320 178" strokeDasharray="6 5" />
      <path d="M -20 168 Q 150 188 320 168" opacity="0.55" strokeDasharray="6 5" />
    </g>
    <g transform="translate(34, 32)" stroke="white" strokeWidth="1.4" fill="none" opacity="0.32">
      <path d="M 0 24 L 0 8" />
      <path d="M 10 24 L 10 4" />
      <path d="M 20 24 L 20 12" />
      <path d="M 30 24 L 30 0" />
      <path d="M -4 24 L 34 24" strokeWidth="1" />
    </g>
  </svg>
);

const BoardgenieVisual = () => (
  <svg
    viewBox="0 0 300 200"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 h-full w-full"
  >
    <g stroke="white" strokeWidth="0.8" fill="none" opacity="0.18">
      <path d="M 20 50 L 32 43 L 44 50 L 44 64 L 32 71 L 20 64 Z" />
      <path d="M 44 50 L 56 43 L 68 50 L 68 64 L 56 71 L 44 64 Z" />
      <path d="M 248 130 L 260 123 L 272 130 L 272 144 L 260 151 L 248 144 Z" />
      <path d="M 272 130 L 284 123 L 296 130 L 296 144 L 284 151 L 272 144 Z" />
    </g>
    <g fill="white" opacity="0.42">
      <g transform="translate(36, 100)">
        <rect
          x="0"
          y="0"
          width="36"
          height="36"
          rx="7"
          fill="none"
          stroke="white"
          strokeWidth="1.6"
        />
        <circle cx="18" cy="18" r="3.2" />
      </g>
      <g transform="translate(132, 38)">
        <rect
          x="0"
          y="0"
          width="36"
          height="36"
          rx="7"
          fill="none"
          stroke="white"
          strokeWidth="1.6"
        />
        <circle cx="9" cy="9" r="3.2" />
        <circle cx="18" cy="18" r="3.2" />
        <circle cx="27" cy="27" r="3.2" />
      </g>
      <g transform="translate(220, 76)">
        <rect
          x="0"
          y="0"
          width="36"
          height="36"
          rx="7"
          fill="none"
          stroke="white"
          strokeWidth="1.6"
        />
        <circle cx="9" cy="9" r="3.2" />
        <circle cx="27" cy="9" r="3.2" />
        <circle cx="18" cy="18" r="3.2" />
        <circle cx="9" cy="27" r="3.2" />
        <circle cx="27" cy="27" r="3.2" />
      </g>
    </g>
  </svg>
);

const projects: Project[] = [
  {
    id: "equip",
    name: "Equip Sport",
    url: "https://equip.sport/",
    stack: ["React Native", "TypeScript", "Fastify", "Supabase", "Bluetooth LE", "Mapbox"],
    gradientFrom: "#ef4d23",
    gradientTo: "#fbbf24",
    visual: <EquipVisual />,
    image: equipImg,
  },
  {
    id: "boardgenie",
    name: "Board Game Genie",
    url: "https://boardgenie.app",
    stack: ["React", "TypeScript", "Bun", "Tailwind", "Supabase", "LLM"],
    gradientFrom: "#7cff6b",
    gradientTo: "#0ea5e9",
    visual: <BoardgenieVisual />,
    image: boardGenieImg,
  },
  {
    id: "dubcircus",
    name: "dubcircus",
    url: "https://dubcircus.io",
    stack: ["React", "TypeScript", "Supabase", "Web Audio", "Twitch chat"],
    gradientFrom: "#1a1026",
    gradientTo: "#f5c542",
    image: dubcircusImg,
  },
];

export function Work() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} id="work" className="frame bg-page-bg">
      <div className="container-narrow section-pad">
        <div
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <span className="eyebrow">
            <span className="num">05 —</span> {t("landing.work.eyebrow")}
          </span>
          <h2 className="section-title">
            <Trans
              i18nKey="landing.work.title"
              components={{ italic: <span className="serif-italic" /> }}
            />
          </h2>
          <p className="section-lede">{t("landing.work.lede")}</p>
        </div>

        <div className="mt-14 grid auto-rows-fr grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
          {projects.map((project, i) => {
            const data = t(`landing.work.projects.${project.id}`, {
              returnObjects: true,
            }) as ProjectData;
            return (
              <div
                key={project.id}
                className={cn(
                  "flex flex-col overflow-hidden rounded-[22px] border border-line bg-surface",
                  "transition-all duration-700",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
                )}
                style={{ transitionDelay: isVisible ? `${i * 100}ms` : "0ms" }}
              >
                <div
                  className="relative flex aspect-[5/3] items-end overflow-hidden p-7"
                  style={
                    project.image
                      ? undefined
                      : {
                          background: `linear-gradient(135deg, ${project.gradientFrom}, ${project.gradientTo})`,
                        }
                  }
                >
                  {project.image ? (
                    <>
                      <img
                        src={project.image}
                        alt={data.imageAlt}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent"
                      />
                    </>
                  ) : (
                    project.visual
                  )}
                  <span
                    className="serif-italic relative leading-none text-white/95"
                    style={{ fontSize: "clamp(36px, 4.6vw, 56px)" }}
                  >
                    {project.name}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-7">
                  <div className="mono text-[11px] tracking-[0.16em] text-ink-3 uppercase">
                    {data.type}
                  </div>
                  <h3 className="mt-3 text-[clamp(22px,2.4vw,30px)] leading-tight font-medium tracking-[-0.025em] text-ink">
                    {data.taglineLead}{" "}
                    <span className="serif-italic text-accent">{data.taglineEm}</span>
                  </h3>
                  <p className="mt-3 text-[14px] leading-[1.6] text-ink-2">{data.desc}</p>
                  <div className="mt-5 mb-6 flex flex-wrap gap-1.5">
                    {project.stack.map((s) => (
                      <span
                        key={s}
                        className="mono rounded-full border border-line bg-surface-2 px-2 py-1 text-[11px] tracking-tight text-ink-2"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center justify-between gap-3 border-t border-line pt-5">
                    <span className="mono text-[11px] tracking-[0.14em] text-ink-3 uppercase">
                      {data.meta}
                    </span>
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-ink transition-colors hover:text-accent"
                    >
                      {t("landing.work.visitLive")}
                      <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div
          className={cn(
            "mt-12 flex justify-center",
            "transition-all delay-200 duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <Link
            to={localizedHref("/work")}
            preload="intent"
            className="mono inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] text-ink-2 uppercase transition-colors hover:text-ink"
          >
            {t("landing.work.viewCaseStudies")}
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>
      </div>
    </section>
  );
}
